import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
 CreditCard,
 CheckCircle,
 XCircle,
 Clock,
 RefreshCw,
 Search,
 ArrowLeft,
 Loader2,
 ListChecks,
 Crown,
 AlertTriangle,
 Ban,
 X,
 ExternalLink,
 Receipt,
 Trash2,
} from 'lucide-react';
import { Button } from '../../../components/shared';
import DataTable from '../../../components/shared/DataTable';
import DatePicker from '../../../components/client/DatePicker';
import {
 deleteAdminSubscriptionPayment,
 getAdminSubscriptionPayments,
} from '../../../services/subscriptionService';
import { useToastContext } from '../../../contexts/ToastContext';
import { AdminPageHeader, AdminGuideCallout } from '../../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../../utils/adminPageGuides';

const isUnassignedToPlan = (row) => {
 const plan = row?.vendor_subscription?.subscription;
 if (plan?.id || plan?.name) return false;
 const vs = row?.vendor_subscription;
 if (!vs) return true;
 if (!vs.subscription_id && !vs.subscription_plan_id) return true;
 return !plan;
};

const STATUS_PILLS = [
 { value: '', label: 'All', countKey: 'total_rows' },
 { value: 'completed', label: 'Completed', countKey: 'completed_count', Icon: CheckCircle },
 { value: 'pending', label: 'Pending', countKey: 'pending_count', Icon: Clock },
 { value: 'failed', label: 'Failed', countKey: 'failed_count', Icon: XCircle },
 { value: 'refunded', label: 'Refunded', countKey: 'refunded_count', Icon: RefreshCw },
];

const money = (amount, currency = 'USD') => {
 const value = Number(amount);
 const code = String(currency || 'USD').toUpperCase();
 if (!Number.isFinite(value)) {
 return `${code} 0.00`;
 }
 try {
 return new Intl.NumberFormat('en-US', {
 style: 'currency',
 currency: code,
 currencyDisplay: 'symbol',
 }).format(value);
 } catch {
 return `${code} ${value.toFixed(2)}`;
 }
};

const MetricCard = ({ label, value, hint, icon: Icon, tone = 'sky' }) => {
 const tones = {
 sky: 'bg-surface-muted text-ink',
 amber: 'bg-amber-50 text-amber-700',
 emerald: 'bg-emerald-50 text-emerald-700',
 rose: 'bg-rose-50 text-rose-700',
 gray: 'bg-gray-100 text-ink-secondary',
 };
 return (
 <div className="bg-surface rounded-lg border border-border p-4 shadow-sm">
 <div className="flex items-start justify-between gap-2">
 <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-tertiary leading-tight">
 {label}
 </span>
 {Icon && (
 <div className={`p-1.5 rounded-lg shrink-0 ${tones[tone]}`}>
 <Icon className="w-4 h-4" />
 </div>
 )}
 </div>
 <p className="text-xl font-bold text-ink mt-2 tabular-nums">{value}</p>
 {hint ? <p className="text-[11px] text-ink-tertiary mt-1 leading-snug">{hint}</p> : null}
 </div>
 );
};

const AdminSubscriptionPayments = () => {
 const queryClient = useQueryClient();
 const { success, error: showError } = useToastContext();
 const [filters, setFilters] = useState({
 status: '',
 subscription_id: '',
 search: '',
 from: '',
 to: '',
 page: 1,
 per_page: 25,
 });
 const [searchInput, setSearchInput] = useState('');
 const [deletingId, setDeletingId] = useState(null);

 useEffect(() => {
 const t = setTimeout(() => {
 setFilters((f) => {
 if (f.search === searchInput) return f;
 return { ...f, search: searchInput, page: 1 };
 });
 }, 300);
 return () => clearTimeout(t);
 }, [searchInput]);

 const queryParams = useMemo(() => {
 const p = {
 page: filters.page,
 per_page: filters.per_page,
 sort_by: 'paid_at',
 sort_order: 'desc',
 };
 if (filters.status) p.status = filters.status;
 if (filters.subscription_id) p.subscription_id = filters.subscription_id;
 if (filters.search.trim()) p.search = filters.search.trim();
 if (filters.from) p.from = filters.from;
 if (filters.to) p.to = filters.to;
 return p;
 }, [filters]);

 const { data, isLoading, error, refetch, isFetching } = useQuery({
 queryKey: ['admin-subscription-payments', queryParams],
 queryFn: async () => getAdminSubscriptionPayments(queryParams),
 placeholderData: (prev) => prev,
 });

 const deleteMutation = useMutation({
 mutationFn: (id) => deleteAdminSubscriptionPayment(id),
 onSuccess: async (res) => {
 success('Deleted', res?.message || 'Unassigned payment removed.');
 setDeletingId(null);
 await queryClient.invalidateQueries({ queryKey: ['admin-subscription-payments'] });
 },
 onError: (err) => {
 setDeletingId(null);
 showError(err?.message || 'Could not delete payment.');
 },
 });

 const handleDeleteUnassigned = (row) => {
 if (!isUnassignedToPlan(row)) return;
 if (
 !window.confirm(
 `Delete unassigned payment #${row.id}? This only removes orphan ledger rows with no plan.`
 )
 ) {
 return;
 }
 setDeletingId(row.id);
 deleteMutation.mutate(row.id);
 };

 const pagination = data?.data;
 const rows = pagination?.data ?? [];
 const summary = data?.summary || {};
 const tabCounts = summary.tab_counts || summary;
 const planOptions = summary.plans || [];
 const selectedPlanName =
 planOptions.find((p) => String(p.id) === String(filters.subscription_id))?.name || '';

 const hasActiveFilters = Boolean(
 filters.status ||
 filters.subscription_id ||
 filters.search.trim() ||
 filters.from ||
 filters.to
 );

 const clearFilters = () => {
 setSearchInput('');
 setFilters({
 status: '',
 subscription_id: '',
 search: '',
 from: '',
 to: '',
 page: 1,
 per_page: 25,
 });
 };

 const statusBadge = (status) => {
 const map = {
 completed: {
 cls: 'bg-emerald-50 text-emerald-800 border-emerald-200/80',
 Icon: CheckCircle,
 label: 'Completed',
 },
 pending: {
 cls: 'bg-amber-50 text-amber-900 border-amber-200/80',
 Icon: Clock,
 label: 'Pending',
 },
 failed: {
 cls: 'bg-red-50 text-red-800 border-red-200/80',
 Icon: XCircle,
 label: 'Failed',
 },
 refunded: {
 cls: 'bg-gray-100 text-gray-800 border-gray-200',
 Icon: RefreshCw,
 label: 'Refunded',
 },
 };
 const cfg = map[status] || map.pending;
 const Icon = cfg.Icon;
 return (
 <span
 className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.cls}`}
 >
 <Icon className="w-3 h-3" />
 {cfg.label}
 </span>
 );
 };

 const columns = [
 {
 key: 'paid_at',
 label: 'When',
 render: (row) => (
 <div className="text-sm">
 <div className="font-medium text-ink">
 {formatDt(row.paid_at || row.created_at)}
 </div>
 <div className="text-[11px] text-ink-tertiary mt-0.5">
 {row.paid_at ? 'Paid' : 'Created'} · #{row.id}
 </div>
 </div>
 ),
 },
 {
 key: 'vendor',
 label: 'Vendor',
 render: (row) => {
 const v = row.vendor_subscription?.vendor;
 const name = v
 ? v.name && String(v.name).trim()
 ? v.name
 : v.email
 : '—';
 const vid = row.vendor_subscription?.vendor_id;
 return (
 <div className="min-w-0 max-w-[220px]">
 <p className="text-sm font-medium text-ink truncate">{name}</p>
 {v?.email && name !== v.email && (
 <p className="text-xs text-ink-tertiary truncate">{v.email}</p>
 )}
 {vid && (
 <Link
 to={`/admin/vendors/${vid}`}
 className="inline-flex items-center gap-1 text-xs text-ink hover:underline font-medium mt-0.5"
 >
 Vendor profile
 <ExternalLink className="w-3 h-3" />
 </Link>
 )}
 </div>
 );
 },
 },
 {
 key: 'plan',
 label: 'Plan',
 render: (row) => {
 const planName = row.vendor_subscription?.subscription?.name;
 return (
 <div className="min-w-0">
 <span className="inline-flex items-center gap-1.5 text-sm text-ink">
 <Crown className="w-3.5 h-3.5 text-amber-500 shrink-0" />
 <span className="truncate">{planName || '—'}</span>
 </span>
 {row.vendor_subscription?.billing_interval && (
 <p className="text-[11px] text-gray-500 mt-0.5 capitalize">
 {String(row.vendor_subscription.billing_interval).replace(/_/g, ' ')}
 </p>
 )}
 {!planName && (
 <p className="text-[11px] text-amber-700 mt-0.5">
 Not assigned to a plan
 </p>
 )}
 </div>
 );
 },
 },
 {
 key: 'amount',
 label: 'Amount',
 render: (row) => (
 <div className="text-right sm:text-left">
 <p className="text-sm font-semibold tabular-nums text-ink">
 {money(row.amount, row.currency)}
 </p>
 {row.amount_reconciled_from_stripe && (
 <p className="text-[10px] text-emerald-600 mt-0.5">
 Matched to Stripe
 </p>
 )}
 </div>
 ),
 },
 {
 key: 'status',
 label: 'Status',
 render: (row) => (
 <div className="space-y-1">
 {statusBadge(row.status)}
 {row.status === 'failed' && row.failure_reason && (
 <p
 className="text-[11px] text-red-600 line-clamp-2 max-w-[160px]"
 title={row.failure_reason}
 >
 {row.failure_reason}
 </p>
 )}
 </div>
 ),
 },
 {
 key: 'method',
 label: 'Reference',
 render: (row) => {
 const ref = row.payment_reference || row.transaction_id;
 return (
 <div className="text-xs text-ink-secondary space-y-0.5 max-w-[200px]">
 <div className="capitalize font-medium">
 {row.payment_method ? String(row.payment_method).replace(/_/g, ' ') : 'Card'}
 </div>
 <div
 className="truncate font-mono text-ink-tertiary"
 title={ref || undefined}
 >
 {ref || '—'}
 </div>
 </div>
 );
 },
 },
 {
 key: 'actions',
 label: 'Actions',
 render: (row) => {
 if (!isUnassignedToPlan(row)) {
 return <span className="text-xs text-gray-400">—</span>;
 }
 const busy = deleteMutation.isPending && deletingId === row.id;
 return (
 <button
 type="button"
 onClick={() => handleDeleteUnassigned(row)}
 disabled={busy || deleteMutation.isPending}
 className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
 title="Delete unassigned payment"
 >
 {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
 Delete
 </button>
 );
 },
 },
 ];

 const completedAmount = Number(summary.completed_amount || 0);
 const netAmount = Number(summary.net_amount || 0);
 const failedCount = Number(summary.failed_count || 0);
 const pendingCount = Number(summary.pending_count || 0);
 const totalRows = Number(summary.total_rows ?? pagination?.total ?? rows.length);

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Subscription payments"
 subtitle="SaaS plan charges from vendors to the platform — separate from client booking payments."
 actions={
 <>
 <Link
 to="/admin/subscriptions"
 className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded-md border border-border bg-surface text-ink hover:bg-surface-muted"
 >
 <ArrowLeft className="w-4 h-4" />
 Plans
 </Link>
 <Button
 type="button"
 variant="outline"
 className="rounded-md"
 onClick={() => refetch()}
 disabled={isFetching}
 >
 <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
 Refresh
 </Button>
 </>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.subscriptionPayments.title}
 steps={ADMIN_PAGE_GUIDES.subscriptionPayments.steps}
 icon={ListChecks}
 />

 {/* Context strip */}
 <div className="rounded-lg border border-border/80 bg-surface-muted/70 px-4 py-3 sm:px-5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
 <div className="flex items-center gap-2 text-ink shrink-0">
 <Receipt className="w-4 h-4" />
 <span className="text-xs font-semibold uppercase tracking-wide">What this is</span>
 </div>
 <p className="text-sm text-ink/80 leading-relaxed">
 Money vendors pay for their VendaList plan. Booking client payments live under{' '}
 <Link to="/admin/payments" className="font-medium underline underline-offset-2">
 Payments
 </Link>
 ; settle vendor earnings under{' '}
 <Link to="/admin/vendor-payouts" className="font-medium underline underline-offset-2">
 Vendor payouts
 </Link>
 .
 </p>
 </div>

 {/* Summary — filter-aware */}
 <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
 <MetricCard
 label="Collected"
 value={isLoading && !data ? '…' : money(completedAmount)}
 hint={`${summary.completed_count ?? 0} completed charge${(summary.completed_count ?? 0) === 1 ? '' : 's'}${hasActiveFilters ? ' (filtered)' : ''}`}
 icon={CreditCard}
 tone="emerald"
 />
 <MetricCard
 label="Net after refunds"
 value={isLoading && !data ? '…' : money(netAmount)}
 hint={
 Number(summary.refunded_amount || 0) > 0
 ? `${money(summary.refunded_amount)} refunded`
 : 'No refunds in this view'
 }
 icon={Crown}
 tone="sky"
 />
 <MetricCard
 label="Needs attention"
 value={isLoading && !data ? '…' : String(failedCount + pendingCount)}
 hint={`${pendingCount} pending · ${failedCount} failed`}
 icon={failedCount > 0 ? AlertTriangle : Clock}
 tone={failedCount > 0 ? 'rose' : pendingCount > 0 ? 'amber' : 'gray'}
 />
 <MetricCard
 label="Charges shown"
 value={isLoading && !data ? '…' : String(totalRows)}
 hint={
 hasActiveFilters
 ? 'Matching your filters'
 : 'All subscription payments'
 }
 icon={ListChecks}
 tone="gray"
 />
 </div>

 <div className="product-panel overflow-hidden">
 {/* Status tabs */}
 <div className="border-b border-border px-4 py-3 sm:px-5">
 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
 role="tablist"
 aria-label="Payment status"
 >
 {STATUS_PILLS.map((tab) => {
 const isActive = filters.status === tab.value;
 const count = tabCounts[tab.countKey];
 const TabIcon = tab.Icon;
 return (
 <button
 key={tab.value || 'all'}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => setFilters((f) => ({ ...f, status: tab.value, page: 1 }))}
 className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${ isActive ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 {TabIcon ? <TabIcon className="w-3.5 h-3.5 opacity-70" /> : null}
 {tab.label}
 {typeof count === 'number' && (
 <span
 className={`text-[11px] tabular-nums px-1.5 py-0.5 rounded-md ${ isActive ? 'bg-gray-100 text-ink-secondary' : 'bg-gray-200/70 text-gray-500' }`}
 >
 {count}
 </span>
 )}
 </button>
 );
 })}
 </div>
 </div>

 <div className="p-4 sm:p-5 space-y-4">
 {/* Filters */}
 <div className="rounded-xl border border-border bg-surface-muted/60 p-4">
 <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
 <p className="text-xs font-semibold uppercase tracking-wide text-ink-tertiary">
 Find a charge
 </p>
 {hasActiveFilters && (
 <button
 type="button"
 onClick={clearFilters}
 className="inline-flex items-center gap-1 text-xs font-medium text-ink-secondary hover:text-gray-900"
 >
 <X className="w-3.5 h-3.5" />
 Clear filters
 </button>
 )}
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
 <div>
 <label className="block text-sm font-medium text-ink-secondary mb-1">
 Search
 </label>
 <div className="relative">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
 <input
 type="search"
 value={searchInput}
 onChange={(e) => setSearchInput(e.target.value)}
 placeholder="Vendor, email, plan, or Stripe reference…"
 className="w-full pl-10 pr-3 py-2 border border-border rounded-xl text-sm bg-surface text-ink focus:ring-2 focus:ring-ink focus:border-ink"
 />
 </div>
 </div>
 <div>
 <label
 htmlFor="subscription-plan-filter"
 className="block text-sm font-medium text-ink-secondary mb-1"
 >
 Plan
 </label>
 <select
 id="subscription-plan-filter"
 value={filters.subscription_id}
 onChange={(e) =>
 setFilters((f) => ({
 ...f,
 subscription_id: e.target.value,
 page: 1,
 }))
 }
 className="w-full px-3 py-2 border border-border rounded-xl text-sm bg-surface text-ink focus:ring-2 focus:ring-ink focus:border-ink"
 >
 <option value="">All plans</option>
 {planOptions.map((plan) => (
 <option key={plan.id} value={plan.id}>
 {plan.name}
 {plan.is_active === false ? ' (inactive)' : ''}
 </option>
 ))}
 </select>
 </div>
 <DatePicker
 label="From"
 value={filters.from}
 onChange={(v) => setFilters((f) => ({ ...f, from: v || '', page: 1 }))}
 />
 <DatePicker
 label="To"
 value={filters.to}
 onChange={(v) => setFilters((f) => ({ ...f, to: v || '', page: 1 }))}
 />
 </div>

 {hasActiveFilters && (
 <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border">
 {filters.status && (
 <FilterChip
 label={`Status: ${filters.status}`}
 onClear={() => setFilters((f) => ({ ...f, status: '', page: 1 }))}
 />
 )}
 {filters.subscription_id && (
 <FilterChip
 label={`Plan: ${selectedPlanName || `#${filters.subscription_id}`}`}
 onClear={() => setFilters((f) => ({ ...f, subscription_id: '', page: 1 }))}
 />
 )}
 {filters.search.trim() && (
 <FilterChip
 label={`Search: ${filters.search.trim()}`}
 onClear={() => {
 setSearchInput('');
 setFilters((f) => ({ ...f, search: '', page: 1 }));
 }}
 />
 )}
 {filters.from && (
 <FilterChip
 label={`From ${filters.from}`}
 onClear={() => setFilters((f) => ({ ...f, from: '', page: 1 }))}
 />
 )}
 {filters.to && (
 <FilterChip
 label={`To ${filters.to}`}
 onClear={() => setFilters((f) => ({ ...f, to: '', page: 1 }))}
 />
 )}
 </div>
 )}
 </div>

 {error && (
 <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3 flex items-start gap-2">
 <Ban className="w-4 h-4 mt-0.5 shrink-0" />
 <span>{error.message || 'Failed to load subscription payments.'}</span>
 </div>
 )}

 {isLoading && !data ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading plan charges…</p>
 </div>
 ) : rows.length === 0 ? (
 <div className="py-16 text-center px-4">
 <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
 <CreditCard className="w-6 h-6 text-gray-400" />
 </div>
 <h3 className="text-sm font-semibold text-ink mb-1">
 No charges found
 </h3>
 <p className="text-sm text-ink-tertiary max-w-sm mx-auto">
 {hasActiveFilters
 ? 'Nothing matches these filters. Clear them or widen the date range.'
 : 'When vendors pay for a plan, those charges will appear here.'}
 </p>
 {hasActiveFilters && (
 <button
 type="button"
 onClick={clearFilters}
 className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink hover:underline"
 >
 Clear filters
 </button>
 )}
 </div>
 ) : (
 <div className={isFetching ? 'opacity-70 transition-opacity' : ''}>
 <DataTable
 columns={columns}
 data={rows}
 loading={false}
 searchable={false}
 filterable={false}
 sortable={false}
 showPagination={false}
 emptyMessage="No subscription payments match your filters."
 className="!border-0 !shadow-none"
 />
 </div>
 )}

 {pagination && pagination.last_page > 1 && (
 <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-border">
 <p className="text-sm text-ink-secondary">
 Showing{' '}
 <span className="font-medium text-ink">
 {(pagination.current_page - 1) * pagination.per_page + 1}
 –
 {Math.min(
 pagination.current_page * pagination.per_page,
 pagination.total
 )}
 </span>{' '}
 of {pagination.total}
 </p>
 <div className="flex gap-2">
 <button
 type="button"
 disabled={pagination.current_page <= 1 || isFetching}
 onClick={() =>
 setFilters((f) => ({ ...f, page: Math.max(1, f.page - 1) }))
 }
 className="px-3 py-1.5 rounded-md border border-border text-sm disabled:opacity-40 bg-surface text-ink"
 >
 Previous
 </button>
 <button
 type="button"
 disabled={
 pagination.current_page >= pagination.last_page || isFetching
 }
 onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
 className="px-3 py-1.5 rounded-md border border-border text-sm disabled:opacity-40 bg-surface text-ink"
 >
 Next
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 );
};

function FilterChip({ label, onClear }) {
 return (
 <span className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-lg bg-surface border border-border text-xs text-ink-secondary">
 {label}
 <button
 type="button"
 onClick={onClear}
 className="p-0.5 rounded-md hover:bg-surface-muted"
 aria-label={`Remove ${label}`}
 >
 <X className="w-3 h-3" />
 </button>
 </span>
 );
}

function formatDt(iso) {
 if (!iso) return '—';
 const d = new Date(iso);
 return d.toLocaleString(undefined, {
 year: 'numeric',
 month: 'short',
 day: 'numeric',
 hour: '2-digit',
 minute: '2-digit',
 });
}

export default AdminSubscriptionPayments;
